import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AccountListItem, Sector } from "../../lib/accounts";
import type {
  CashflowKind,
  CashflowRhythm,
  CreatePositionInput,
  PositionDto,
  UpdatePositionInput,
} from "../../lib/cashflow";
import { CategorySelect } from "./CategorySelect";

const RHYTHMS: CashflowRhythm[] = [
  "MONATLICH",
  "QUARTALSWEISE",
  "HALBJAEHRLICH",
  "JAEHRLICH",
  "EINMALIG",
];
const SECTORS: Sector[] = ["HAUSHALT", "IMMOBILIE", "UNZUGEORDNET"];

interface PositionFormProps {
  mode: "create" | "edit";
  initial?: PositionDto;
  accounts: AccountListItem[];
  onSubmit: (input: CreatePositionInput | UpdatePositionInput) => Promise<void>;
  submitting?: boolean;
  serverError?: string;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) {
    throw new Error("invalid_amount");
  }
  return Math.round(n * 100);
}

function formatEuroDe(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function PositionForm({
  mode,
  initial,
  accounts,
  onSubmit,
  submitting,
  serverError,
}: PositionFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState(initial?.name ?? "");
  const [kind, setKind] = useState<CashflowKind>(initial?.kind ?? "AUSGABE");
  const [amount, setAmount] = useState(initial ? formatEuroDe(initial.amountCents) : "");
  const [rhythm, setRhythm] = useState<CashflowRhythm>(initial?.rhythm ?? "MONATLICH");
  const [firstDueMonth, setFirstDueMonth] = useState(
    initial?.firstDueMonth ?? new Date().toISOString().slice(0, 7),
  );
  const [endMonth, setEndMonth] = useState<string>(initial?.endMonth ?? "");
  const [sector, setSector] = useState<Sector>(initial?.sector ?? "UNZUGEORDNET");
  const [sectorDirty, setSectorDirty] = useState(false);
  const [targetSector, setTargetSector] = useState<Sector | "">(initial?.targetSector ?? "");
  const [countsToSavings, setCountsToSavings] = useState<boolean>(
    initial?.countsToSavings ?? false,
  );
  const [categoryId, setCategoryId] = useState<string | null>(initial?.categoryRef?.id ?? null);
  const legacyCategoryString = initial?.categoryRef ? null : (initial?.category ?? null);
  const [accountId, setAccountId] = useState<string>(initial?.accountId ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [kindFlipConfirmed, setKindFlipConfirmed] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const initialKind = initial?.kind;
  const kindChanged = mode === "edit" && initialKind !== undefined && kind !== initialKind;

  // Sector-Vorschlag aus Konto: nur, solange User nicht manuell geaendert hat.
  useEffect(() => {
    if (sectorDirty) return;
    if (!accountId) return;
    const acc = accounts.find((a) => a.id === accountId);
    if (acc) setSector(acc.sector);
  }, [accountId, accounts, sectorDirty]);

  // EINMALIG: endMonth ausblenden + clearen.
  useEffect(() => {
    if (rhythm === "EINMALIG" && endMonth) setEndMonth("");
  }, [rhythm, endMonth]);

  // Wenn kind nicht TRANSFER: targetSector zuruecksetzen.
  useEffect(() => {
    if (kind !== "TRANSFER" && targetSector !== "") setTargetSector("");
  }, [kind, targetSector]);

  // Wenn source == target nach Sektor-Wechsel: targetSector zuruecksetzen.
  useEffect(() => {
    if (kind === "TRANSFER" && targetSector === sector) setTargetSector("");
  }, [kind, sector, targetSector]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    let amountCents: number;
    try {
      amountCents = parseEuroDe(amount);
    } catch {
      setLocalError(t("cashflow.errors.invalid_amount"));
      return;
    }
    if (kindChanged && !kindFlipConfirmed) {
      setLocalError(t("cashflow.form.kind_change_confirm_required"));
      return;
    }
    if (kind === "TRANSFER") {
      if (!targetSector) {
        setLocalError(t("cashflow.form.errors.transfer_target_required"));
        return;
      }
      if (targetSector === sector) {
        setLocalError(t("cashflow.form.errors.transfer_target_same_as_source"));
        return;
      }
    }
    const base: CreatePositionInput = {
      name: name.trim(),
      kind,
      amountCents,
      currency: "EUR",
      rhythm,
      firstDueMonth,
      sector,
    };
    if (kind === "TRANSFER" && targetSector) base.targetSector = targetSector;
    if (rhythm !== "EINMALIG" && endMonth) base.endMonth = endMonth;
    if (categoryId) base.categoryId = categoryId;
    if (accountId) base.accountId = accountId;
    if (notes.trim()) base.notes = notes.trim();
    // countsToSavings nur bei AUSGABE sinnvoll; immer mitsenden damit Patch unten
    // auch ein "false setzen" abdeckt.
    base.countsToSavings = kind === "AUSGABE" ? countsToSavings : false;
    await onSubmit(base);
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset className="space-y-2">
        <legend className="font-semibold">{t("cashflow.form.section.what")}</legend>
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.name")}</span>
          <input
            className="w-full border rounded px-2 py-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            maxLength={80}
          />
        </label>
        <div className="flex gap-4">
          <label>
            <input
              type="radio"
              name="kind"
              value="EINNAHME"
              checked={kind === "EINNAHME"}
              onChange={() => setKind("EINNAHME")}
            />{" "}
            {t("cashflow.kind.EINNAHME")}
          </label>
          <label>
            <input
              type="radio"
              name="kind"
              value="AUSGABE"
              checked={kind === "AUSGABE"}
              onChange={() => setKind("AUSGABE")}
            />{" "}
            {t("cashflow.kind.AUSGABE")}
          </label>
          <label>
            <input
              type="radio"
              name="kind"
              value="TRANSFER"
              checked={kind === "TRANSFER"}
              onChange={() => setKind("TRANSFER")}
            />{" "}
            {t("cashflow.kind.TRANSFER")}
          </label>
        </div>
        {kind === "TRANSFER" && (
          <p className="text-xs text-slate-600">{t("cashflow.form.transfer_hint")}</p>
        )}
        {kindChanged && (
          <div className="border border-amber-300 bg-amber-50 rounded p-2 text-sm">
            <p>{t("cashflow.form.kind_change_warning")}</p>
            <label className="mt-1 inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={kindFlipConfirmed}
                onChange={(e) => setKindFlipConfirmed(e.target.checked)}
              />
              {t("cashflow.form.kind_change_confirm")}
            </label>
          </div>
        )}
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.amount")}</span>
          <input
            className="w-full border rounded px-2 py-1"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.rhythm")}</span>
          <select
            className="w-full border rounded px-2 py-1"
            value={rhythm}
            onChange={(e) => setRhythm(e.target.value as CashflowRhythm)}
          >
            {RHYTHMS.map((r) => (
              <option key={r} value={r}>
                {t(`cashflow.rhythm.${r}`)}
              </option>
            ))}
          </select>
        </label>
        <div className="block">
          <span className="text-sm">{t("cashflow.form.label.category")}</span>
          <CategorySelect
            value={categoryId}
            direction={kind === "EINNAHME" ? "INCOME" : kind === "TRANSFER" ? "TRANSFER" : "EXPENSE"}
            onChange={setCategoryId}
          />
          {legacyCategoryString && (
            <p className="text-xs italic text-slate-600 mt-1">
              {t("cashflow.form.category.legacy_hint", {
                defaultValue: "Alte Text-Kategorie. Bitte auf eine Kategorie mappen:",
              })}{" "}
              <span className="font-mono">{legacyCategoryString}</span>
            </p>
          )}
        </div>
        {kind === "AUSGABE" && (
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={countsToSavings}
              onChange={(e) => setCountsToSavings(e.target.checked)}
            />
            <span>{t("cashflow.form.label.countsToSavings")}</span>
          </label>
        )}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="font-semibold">{t("cashflow.form.section.when")}</legend>
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.firstDueMonth")}</span>
          <input
            type="month"
            className="border rounded px-2 py-1"
            value={firstDueMonth}
            onChange={(e) => setFirstDueMonth(e.target.value)}
            required
          />
        </label>
        {rhythm !== "EINMALIG" && (
          <label className="block">
            <span className="text-sm">{t("cashflow.form.label.endMonth")}</span>
            <input
              type="month"
              className="border rounded px-2 py-1"
              value={endMonth}
              onChange={(e) => setEndMonth(e.target.value)}
              min={firstDueMonth}
            />
          </label>
        )}
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="font-semibold">{t("cashflow.form.section.where")}</legend>
        <label className="block">
          <span className="text-sm">
            {kind === "TRANSFER"
              ? t("cashflow.form.label.sourceSector")
              : t("cashflow.form.label.sector")}
          </span>
          <select
            className="w-full border rounded px-2 py-1"
            value={sector}
            onChange={(e) => {
              setSector(e.target.value as Sector);
              setSectorDirty(true);
            }}
          >
            {SECTORS.map((s) => (
              <option key={s} value={s}>
                {t(`accounts.sector.${s}`)}
              </option>
            ))}
          </select>
        </label>
        {kind === "TRANSFER" && (
          <label className="block">
            <span className="text-sm">{t("cashflow.form.label.targetSector")}</span>
            <select
              className="w-full border rounded px-2 py-1"
              value={targetSector}
              onChange={(e) => setTargetSector(e.target.value as Sector)}
              required
            >
              <option value="">{t("cashflow.form.targetSector_placeholder")}</option>
              {SECTORS.filter((s) => s !== sector).map((s) => (
                <option key={s} value={s}>
                  {t(`accounts.sector.${s}`)}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.account")}</span>
          <select
            className="w-full border rounded px-2 py-1"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">{t("cashflow.form.account_none")}</option>
            {accounts
              .filter((a) => a.isActive)
              .map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm">{t("cashflow.form.label.notes")}</span>
          <textarea
            className="w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={3}
          />
        </label>
      </fieldset>

      {(localError ?? serverError) && (
        <div className="text-red-700 text-sm">{localError ?? serverError}</div>
      )}

      <button
        type="submit"
        className="px-4 py-2 bg-blue-600 text-white rounded"
        disabled={submitting === true || (kindChanged && !kindFlipConfirmed)}
      >
        {submitting === true ? t("common.submitting") : t("common.save")}
      </button>
    </form>
  );
}
